import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Formalite, baserowAPI } from '../services/baserowAPI';
import { useAuth } from './AuthContext';

interface FormalitesContextType {
  allData: Formalite[];
  loading: boolean;
  error: string | null;
  loadAllData: () => Promise<void>;
  createRow: (data: Partial<Formalite>) => Promise<Formalite | null>;
  updateRow: (rowId: number, data: Partial<Formalite>) => Promise<Formalite | null>;
  deleteRow: (rowId: number) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const FormalitesContext = createContext<FormalitesContextType | undefined>(undefined);

export const FormalitesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [allData, setAllData] = useState<Formalite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  // Charger TOUTES les données en une seule fois avec pagination manuelle
  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allRows: Formalite[] = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 200; // Charger par lots de 200

      while (hasMore) {
        const response = await baserowAPI.getRows<Formalite>(757, {
          page,
          size: pageSize,
          order_by: '-field_7264', // Date de création descendante
        });

        allRows.push(...(response.results || []));
        hasMore = !!response.next;
        page++;
      }

      setAllData(allRows);
      console.log(`✅ ${allRows.length} formalités chargées en cache`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des données';
      setError(errorMessage);
      console.error('Erreur loadAllData:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createRow = useCallback(async (newData: Partial<Formalite>): Promise<Formalite | null> => {
    try {
      setError(null);
      const newRow = await baserowAPI.createRow<Formalite>(757, newData);
      
      // Ajouter la nouvelle ligne aux données locales
      setAllData(prev => [newRow, ...prev]);
      console.log('✅ Formalité créée et ajoutée au cache');
      
      return newRow;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la création';
      setError(errorMessage);
      console.error('Erreur createRow:', err);
      return null;
    }
  }, []);

  const updateRow = useCallback(async (rowId: number, updateData: Partial<Formalite>): Promise<Formalite | null> => {
    try {
      setError(null);
      const updatedRow = await baserowAPI.updateRow<Formalite>(757, rowId, updateData);
      
      // Mettre à jour les données locales
      setAllData(prev => prev.map(item => 
        item.id === rowId ? { ...item, ...updatedRow } : item
      ));
      console.log('✅ Formalité mise à jour dans le cache');
      
      return updatedRow;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(errorMessage);
      console.error('Erreur updateRow:', err);
      return null;
    }
  }, []);

  const deleteRow = useCallback(async (rowId: number): Promise<boolean> => {
    try {
      setError(null);
      await baserowAPI.deleteRow(757, rowId);
      
      // Supprimer la ligne des données locales
      setAllData(prev => prev.filter(item => item.id !== rowId));
      console.log('✅ Formalité supprimée du cache');
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      setError(errorMessage);
      console.error('Erreur deleteRow:', err);
      return false;
    }
  }, []);

  const refresh = useCallback(async () => {
    console.log('🔄 Rechargement des données...');
    await loadAllData();
  }, [loadAllData]);

  // Charger uniquement quand l'utilisateur est authentifié
  useEffect(() => {
    if (!isAuthenticated) {
      setAllData([]);
      setError(null);
      setLoading(false);
      return;
    }

    loadAllData();
  }, [isAuthenticated, loadAllData]);

  return (
    <FormalitesContext.Provider
      value={{
        allData,
        loading,
        error,
        loadAllData,
        createRow,
        updateRow,
        deleteRow,
        refresh,
      }}
    >
      {children}
    </FormalitesContext.Provider>
  );
};

export const useFormalitesCache = () => {
  const context = useContext(FormalitesContext);
  if (context === undefined) {
    throw new Error('useFormalitesCache must be used within a FormalitesProvider');
  }
  return context;
};
